import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { listAccounts, listCategorizations } from "../api/transactions";
import type { AccountBanner, Categorization } from "../types/transaction";
import { TransactionStack } from "./TransactionStack";
import { ErrorMessage } from "./ErrorMessage";
import { ReviewBanners, type BannerHighlight } from "./review/ReviewBanners";
import { refusalSentence } from "../hooks/useServerAnswer";

interface TransactionListProps {
  clientId: number;
  refreshKey: number;
}

export function TransactionList({ clientId, refreshKey }: TransactionListProps): ReactNode {
  const [items, setItems] = useState<Categorization[]>([]);
  const [total, setTotal] = useState(0);
  const [ledgerReadable, setLedgerReadable] = useState(false);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [banners, setBanners] = useState<AccountBanner[]>([]);
  const [highlight, setHighlight] = useState<BannerHighlight | null>(null);
  const accountsLoaded = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Load all transactions (pending and reviewed) so we can show both sections
      const catPromise = listCategorizations(clientId, undefined, 200, 0);
      const acctPromise = accountsLoaded.current ? Promise.resolve(null) : listAccounts(clientId);

      const [catResponse, acctList] = await Promise.all([catPromise, acctPromise]);
      setItems(catResponse.items);
      setTotal(catResponse.total);
      setLedgerReadable(catResponse.ledger_readable);
      setBanners(catResponse.banners ?? []);
      if (acctList) {
        setAccounts(acctList.accounts);
        setLabels(acctList.labels);
        accountsLoaded.current = true;
      }
    } catch (err) {
      setError(refusalSentence(err));
    } finally {
      setLoading(false);
    }
  }, [clientId, refreshKey]);

  useEffect(() => {
    void load();
  }, [load]);

  // A held account's refusal on a row: bring its banner into view when one
  // carries the sentence (true), else let the deck print it (false).
  const pointAtHeldBanner = useCallback(
    (last6: string) => {
      const held = banners.filter((b) => b.kind === "held");
      const match = held.some((b) => last6 === "" || b.last6 === last6);
      if (match) setHighlight((prev) => ({ last6, key: (prev?.key ?? 0) + 1 }));
      return match;
    },
    [banners],
  );

  if (loading && items.length === 0) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        Loading transactions...
      </div>
    );
  }

  if (error) {
    return (
      <div className="transaction-error">
        <ErrorMessage message={error} onDismiss={() => setError("")} />
        <button type="button" className="retry-button" onClick={() => void load()}>
          Try again
        </button>
      </div>
    );
  }

  const bannerList = <ReviewBanners banners={banners} highlight={highlight} />;

  if (items.length === 0 && total === 0) {
    return (
      <div className="empty-state">
        {bannerList}
        <div className="empty-state-icon">&#128196;</div>
        <p className="empty-state-text">No transactions yet</p>
        <p className="empty-state-hint">Upload a bank statement above to get started.</p>
      </div>
    );
  }

  return (
    <>
      {bannerList}
      <TransactionStack
        items={items}
        accounts={accounts}
        labels={labels}
        clientId={clientId}
        onUpdated={() => void load()}
        total={total}
        ledgerReadable={ledgerReadable}
        onHeldRefusal={pointAtHeldBanner}
      />
    </>
  );
}
